/**
 * @file        src/layout/AdminLayout.jsx
 * @description Shell for every authed admin route: aurora + sidebar + topbar.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-09 Claude — Hamburger now actually toggles the sidebar. The CSS
 *                          had a `.app.collapsed` mini-rail that nothing applied,
 *                          so on DESKTOP the button did nothing (the `open` state
 *                          only drives the mobile off-canvas). Now it collapses
 *                          the rail on desktop and slides the off-canvas on
 *                          mobile, with a backdrop to tap-close on mobile.
 *   - 2026-08-04 Claude — Global <LoadBar />: slim top progress bar shown while
 *                          any backend request is in flight, so slow APIs never
 *                          read as a frozen page.
 */
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import Aurora from '../components/Aurora';
import LoadBar from '../components/LoadBar';
import Sidebar from './Sidebar';
import Topbar from './Topbar';

const isMobile = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches;

export default function AdminLayout() {
  // Two independent states because the sidebar has two modes:
  //   desktop → `collapsed` shrinks it to the 92px icon rail (.app.collapsed)
  //   mobile  → `open` slides the off-canvas drawer in (.sidebar.open)
  // The hamburger drives whichever applies at the current viewport.
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(false);

  const toggle = () => (isMobile() ? setOpen((o) => !o) : setCollapsed((c) => !c));

  return (
    <>
      <Aurora />
      <LoadBar />
      <div className={`app ${collapsed ? 'collapsed' : ''}`}>
        <Sidebar open={open} collapsed={collapsed} onClose={() => setOpen(false)} />
        {/* Mobile-only backdrop: tap anywhere off the drawer to close it. */}
        {open && <div className="side-backdrop" onClick={() => setOpen(false)} />}
        <main className="main">
          <Topbar onToggleSidebar={toggle} />
          <section className="adm-view">
            <Outlet />
          </section>
        </main>
      </div>
    </>
  );
}
